import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "react-hook-form";
import produce from "immer";

import "./Manufacturers.scss";
import { ErrorBoundary, ErrorMessage, FormInput, Loader, Page, PageHeader } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { IManufacturer } from "interfaces";
import { ManufacturersService, parseAxiosError } from "services";

interface IManufacturerPageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    createStatus?: AsyncStatus;
    createError?: string;
    manufacturers?: IManufacturer[];
    deleteManufacturer?: IManufacturer;
}

type IManufacturerForm = {
    name: string;
};

export function Manufacturers() {

    const [pageState, setPageState] = useState<IManufacturerPageState>({
        loadStatus: AsyncStatus.Loading,
        createStatus: AsyncStatus.Initial,
        deleteStatus: AsyncStatus.Initial,
        manufacturers: [],
    });
    const { loadStatus, loadError, deleteStatus, deleteError, createStatus, createError, manufacturers, deleteManufacturer } = pageState;
    
    const form = useForm<IManufacturerForm>({ mode: "onTouched" });

    // create modal control
    const [createShow, setCreateShow] = useState(false);
    const handleCreateClose = () => setCreateShow(false);
    const showCreateModal = () => {
        setCreateShow(true);
    };

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteManufacturerModal = (man) => {
        setPageState(
            produce((draft) => {
                draft.deleteManufacturer = man;
            })
        );
        setDeleteShow(true);
    };

    // data is preloaded in app.tsx, so just assign from service
    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;
        
        setPageState(
            produce((draft) => {
                draft.loadStatus = AsyncStatus.Success;
                draft.manufacturers = ManufacturersService.manufacturers;
            })
        );
    });
 
    // Soft deletes the Manufacturer in the DB
    function onDeleteManufacturer(man: IManufacturer) {
        if (deleteStatus === AsyncStatus.Loading) return;

        setPageState(
            produce((draft) => {
                draft.deleteManufacturer = man;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )

        ManufacturersService.deleteManufacturer(man.id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.deleteManufacturer = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                        draft.manufacturers = ManufacturersService.manufacturers;
                    })
                )
                setDeleteShow(false);
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                );
            });
        
    }

    const onCreateManufacturer = () => {
        if (createStatus === AsyncStatus.Loading) return;

        form.handleSubmit((formData: IManufacturerForm) => {

            const manufacturerNames = manufacturers.map((m) => m.name);
            if (manufacturerNames.includes(formData.name)) {
                setPageState(produce(draft => {
                    draft.createError = `${formData.name} already exists.`;
                    draft.createStatus = AsyncStatus.Error;
                }));
                return;
            }

            // Setstate to loading
            setPageState(produce(draft => {
                draft.createError = '';
                draft.createStatus = AsyncStatus.Loading;
            }));
            
            ManufacturersService.postManufacturer(formData)
                .then(() => {
                    setPageState(produce(draft => {
                        draft.createError = '';
                        draft.createStatus = AsyncStatus.Success;
                        draft.manufacturers = ManufacturersService.manufacturers;
                    }));
                    setCreateShow(false);
                })
                .catch((err) => {
                    setPageState(
                        produce((draft) => {
                            draft.createError = parseAxiosError(err);
                            draft.createStatus = AsyncStatus.Error;
                        })
                    );
                });
        })();
    };

    return (<>
        <Page>
            <PageHeader title="Manufacturers" right={<Button onClick={showCreateModal}>{'New Manufacturer'}</Button>} />

            <div className="row manufacturers_colHeaders">
                <div className="manufacturers_colHeader manufacturers_colName">Name</div>
            </div>
            
            <div className="manufacturers_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        manufacturers.length > 0 && manufacturers.map((man) => {
                            return (
                                <div className="manufacturers_listItem" key={man.id}>
                                    <div className="manufacturers_colName">{man.name}</div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            disabled={man.isDeleted}
                                            onClick={() => showDeleteManufacturerModal(man)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        manufacturers.length === 0 && (
                            <div className="manufacturers_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="manufacturers_listEmptyIcon"
                                />
                                <div className="manufacturers_listEmptyText">
                                    No manufacturers found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Delete Manufacturer
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this manufacturer?</p>
                <div className="manufacturers_deleteHeader">
                    Name: {deleteManufacturer && deleteManufacturer.name}<br/>
                    ID: {deleteManufacturer && deleteManufacturer.id}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" loading={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteManufacturer(deleteManufacturer)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>

        <Modal show={createShow} onClose={handleCreateClose}>
            <ModalHeader>
                Create Manufacturer
            </ModalHeader>
            <ModalBody>
                <div>
                    {createStatus === AsyncStatus.Error && (
                        <ErrorMessage message={createError} />
                    )}
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Manufacturer"
                                name="name"
                                registerOptions={{ required: true }}
                            />
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="outlined" disabled={createStatus === AsyncStatus.Loading} onClick={handleCreateClose}>
                    Cancel
                </Button>
                <Button loading={createStatus === AsyncStatus.Loading} disabled={!form.formState.isValid} onClick={onCreateManufacturer}>
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    </>);
}
